import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "../components/ui/button";
import { formatElapsed } from "../domain/format";
import { BrandMark } from "./BrandMark";

/** Past this, keep-waiting becomes an offer to retry (SPEC-APP.md §2.4). */
const RETRY_AFTER_S = 60;

export interface WakingUpScreenProps {
  /** Seconds the bootstrap request has been pending, when the caller tracks it. */
  startedAt?: number;
  onRetry: () => void;
}

/**
 * The dev API scales to zero. A slow first answer is NEVER the generic failure.
 */
export function WakingUpScreen({ startedAt, onRetry }: WakingUpScreenProps) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const elapsed = Math.max(0, Math.floor((now - (startedAt ?? now)) / 1000));

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-lg">
      <div className="w-full max-w-[24rem] text-center">
        <BrandMark className="mx-auto h-6 w-8 text-primary" />
        <h1 className="type-section mt-md text-foreground">
          Waking the LevelLoad service — usually under 30 seconds
        </h1>
        <p className="type-body mt-sm flex items-center justify-center gap-xs text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          <span className="tabular-nums">{formatElapsed(elapsed)}</span>
        </p>
        {elapsed >= RETRY_AFTER_S ? (
          <Button className="mt-lg" onClick={onRetry}>
            Retry
          </Button>
        ) : (
          <p className="type-caption mt-lg text-muted-foreground">Still waiting…</p>
        )}
      </div>
    </div>
  );
}
